/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, October 27th 2021, 8:59:04 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */
import React from "react";
import { useAPITerminateUserSession } from "authentication/services/AuthAPI";
import { useAPIFetchUserDetails } from "users/services/UserAPI";
import { User } from "lib/schemas";
import { getBrowserObjectStorageFunctions } from "lib/utils";

export interface IUserContext {
  loggedInUser: User;
  saveLoggedInUser: (loggedInUser: User) => void;
  logoutUser: () => void;
  clearLoggedInUser: () => void;
}
export const UserContext = React.createContext<IUserContext>({
  loggedInUser: undefined,
  saveLoggedInUser: undefined,
  logoutUser: undefined,
  clearLoggedInUser: undefined,
});

export const UserContextProvider = ({ children }) => {
  const { getObjectFromBrowserStorage, saveObjectToBrowserStorage } = getBrowserObjectStorageFunctions<User>();

  const [loggedInUser, setLoggedInUser] = React.useState<User>(getObjectFromBrowserStorage("loggedInUser"));

  const saveLoggedInUser = (loggedInUser: User) => {
    setLoggedInUser(loggedInUser);
    saveObjectToBrowserStorage("loggedInUser", loggedInUser);
  };

  const clearLoggedInUser = () => {
    setLoggedInUser(null); // Setting logged in user to null. Note: Since our ProtectedRoute uses this context as state, it will automatically redirect to login URL from any URL as soon as this state changes to null
    localStorage.removeItem("loggedInUser");
  };

  const { terminateUserSession } = useAPITerminateUserSession();

  const logoutUser = () => {
    terminateUserSession({ setValidAPIResponse: null }); // Logout/Kill the session
    clearLoggedInUser();
  };

  const { apiItem: userDetails, fetchItem: fetchUserDetails } = useAPIFetchUserDetails();

  React.useEffect(() => {
    fetchUserDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (userDetails?.id) saveLoggedInUser(userDetails);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails?.id]);

  return (
    <UserContext.Provider value={{ loggedInUser, saveLoggedInUser, logoutUser, clearLoggedInUser }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => {
  return React.useContext(UserContext);
};
